import {
  Button,
  ChakraAsyncSelect,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateCreditUtilizationApi, useSearchBnplFacilitiesApi, useSearchSupplierStatementsApi } from 'apis/bnpl';
import { useSearchInvoiceScansApi } from 'apis/invoice-scanning';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface CreateCreditUtilizationModalProps {
  trigger: JSX.Element;
}

const sourceTypeOptions = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'statement', label: 'Statement' },
];

type TSourceTypeOption = { value: string; label: string };
type TFacilityOption = { value: number; label: string; restaurantId?: number };
type TOption = { value: number; label: string };

interface FormInputs {
  facility: TFacilityOption | null;
  sourceType: TSourceTypeOption | null;
  source: TOption | null;
  amount: string;
  notes: string;
}

const defaultValues: FormInputs = {
  facility: null,
  sourceType: null,
  source: null,
  amount: '',
  notes: '',
};

export const CreateCreditUtilizationModal = ({ trigger }: CreateCreditUtilizationModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate, isLoading } = useCreateCreditUtilizationApi();
  const { loadOptions: loadFacilityOptions } = useSearchBnplFacilitiesApi();
  const { loadOptions: makeInvoiceLoadOptions } = useSearchInvoiceScansApi();
  const { loadOptions: makeStatementLoadOptions } = useSearchSupplierStatementsApi();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const selectedFacility = watch('facility');
  const selectedSourceType = watch('sourceType');

  const onCloseModal = () => {
    reset(defaultValues);
    onClose();
  };

  const sourceLoadOptions = useMemo(() => {
    if (!selectedFacility || !selectedSourceType) return undefined;

    if (selectedSourceType.value === 'invoice' && selectedFacility.restaurantId) {
      return makeInvoiceLoadOptions(selectedFacility.restaurantId);
    }
    if (selectedSourceType.value === 'statement') {
      return makeStatementLoadOptions(selectedFacility.value);
    }
    return undefined;
  }, [selectedFacility, selectedSourceType, makeInvoiceLoadOptions, makeStatementLoadOptions]);

  const onSubmit = (data: FormInputs) => {
    if (!data.facility || !data.sourceType || !data.source) return;

    mutate(
      {
        body: {
          facilityId: data.facility.value,
          sourceType: data.sourceType.value as 'invoice' | 'statement',
          sourceId: data.source.value,
          amount: parseFloat(data.amount),
          notes: data.notes || undefined,
        },
      },
      { onSuccess: () => onCloseModal() }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Credit Utilization</ModalHeader>
          <ModalBody>
            <Stack as='form' id='createCreditUtilizationForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                control={control}
                name='facility'
                rules={{ required: 'Credit facility is required.' }}
                render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>Credit Facility</FormLabel>
                    <ChakraAsyncSelect
                      onChange={(option: any) => {
                        onChange(option);
                        setValue('source', null);
                      }}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      loadOptions={loadFacilityOptions}
                      placeholder='Type to search active facilities...'
                      isClearable
                      noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                        inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No active facilities found'
                      }
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name='sourceType'
                rules={{ required: 'Source type is required.' }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>Source Type</FormLabel>
                    <ChakraSelect
                      placeholder='Select source type'
                      options={sourceTypeOptions}
                      onChange={(option: any) => {
                        onChange(option);
                        setValue('source', null);
                      }}
                      onBlur={onBlur}
                      value={value}
                      isClearable
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <Controller
                control={control}
                name='source'
                rules={{ required: 'Source is required.' }}
                render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>
                      {selectedSourceType?.value === 'invoice'
                        ? 'Invoice Scan'
                        : selectedSourceType?.value === 'statement'
                          ? 'Supplier Statement'
                          : 'Source'}
                    </FormLabel>
                    <ChakraAsyncSelect
                      key={`${selectedFacility?.value}-${selectedSourceType?.value}`}
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      loadOptions={sourceLoadOptions}
                      placeholder={
                        !selectedFacility
                          ? 'Select a facility first...'
                          : !selectedSourceType
                            ? 'Select a source type first...'
                            : 'Type to search...'
                      }
                      isClearable
                      isDisabled={!selectedFacility || !selectedSourceType}
                      defaultOptions={!!sourceLoadOptions}
                      noOptionsMessage={() => 'No records found'}
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <FormControl isInvalid={!!errors.amount} isRequired>
                <FormLabel>Amount (AED)</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min={0.01}
                  {...register('amount', {
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' },
                    validate: (value) => !isNaN(parseFloat(value)) || 'Please enter a valid number',
                  })}
                />
                <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Notes (optional)</FormLabel>
                <Textarea {...register('notes')} placeholder='Optional notes' />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button variant='solid' colorScheme='red' type='submit' form='createCreditUtilizationForm' isLoading={isLoading}>
                Create
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
